'use client'

import { useMemo } from 'react'

import { groupBy, sumBy } from 'lodash'

import { Segment } from '~/components/component-library/SegmentedAssetsBar'
import { SupportedChainId } from '~/constants/chains'
import { useMultipleTokensMetadata } from '~/hooks/useTokenMetadata'
import { IAddress, OrgSmartAccount } from '~/types'
import { TokenBalance } from '~/utils/account'
import {
  getCustomSymbolOverride,
  getTokenColor,
  isSmallValue,
} from '~/utils/tokens'

type SegmentSourceBalance = {
  address: IAddress
  chainId: SupportedChainId
  amount: bigint
  symbol: string
  usdValue?: number
  ownerAddress?: IAddress
}

// Shared by the accounts list, and the card assets bar
export const buildAssetSegments = ({
  balances,
  showSmallBalances = false,
}: {
  balances: SegmentSourceBalance[]
  showSmallBalances?: boolean
}): { segments: Segment[]; totalValue: number } => {
  // Filter out small balances unless showSmallBalances is enabled
  const filteredBalances = showSmallBalances
    ? balances
    : balances.filter((balance) => !isSmallValue(balance.usdValue ?? 0))

  const groupedBalances = groupBy(
    filteredBalances,
    (balance) => getCustomSymbolOverride(balance.symbol) ?? balance.symbol,
  )

  const segments = Object.entries(groupedBalances).map(
    ([symbol, groupBalances]) => {
      return {
        name: symbol,
        color: getTokenColor(symbol),
        value: sumBy(groupBalances, (b) => b.usdValue ?? 0),
        items: groupBalances.map((assetBalance) => ({
          symbol: assetBalance.symbol,
          address: assetBalance.address,
          chainId: assetBalance.chainId,
          amount: assetBalance.amount,
          value: assetBalance.usdValue,
          account: assetBalance.ownerAddress,
        })),
      } satisfies Segment
    },
  )

  // Calculate total from filtered segments so bar fills 100%
  const totalValue = sumBy(segments, 'value')

  return { segments, totalValue }
}

export const useAssetSegments = (
  smartAccounts: OrgSmartAccount[],
  balancesMap: Record<string, { balances: TokenBalance[]; totalValue: number }>,
  showSmallBalances = false,
): { segments: Segment[]; totalValue: number } => {
  const flatBalances = useMemo(() => {
    return smartAccounts.flatMap(
      (account) => balancesMap[account.address]?.balances ?? [],
    )
  }, [smartAccounts, balancesMap])
  const tokenMetadata = useMultipleTokensMetadata(flatBalances)

  return useMemo(
    () =>
      buildAssetSegments({
        balances: flatBalances.map((balance, index) => ({
          ...balance,
          symbol: tokenMetadata[index].symbol,
        })),
        showSmallBalances,
      }),
    [flatBalances, tokenMetadata, showSmallBalances],
  )
}
